import * as enabledApi from '@shared/api/enabledPlayApi';
import { StopAllOutputsCommand } from '@shared/constants/CommandValues';
import { isLicensedProfile, isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import { DEVICE_CURRENT_PROFILE } from '@shared/constants/StorageValues';
import IUserAccess from '@shared/models/components/IUserAccess';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import _ from 'lodash';
import { create } from 'zustand';
import { publish, subscribe } from '../helpers/EventCallback';

/**
 * Container managing the device based on routing of /deviceId.
 * In the panel window, CurrentDeviceContainer.id is the same as deviceContainer.outputDeviceId.
 */

interface CurrentDeviceState {
    // State
    currentApp: string | undefined;
    currentDomain: string | undefined;
    previousApp: string | undefined;
    currentProfile: UserDeviceProfileModel | null;
    device: UserDeviceModel | undefined;
    deviceTypeId: string | undefined;
    errors: string[];
    favoritesDirty: boolean;
    id: string | undefined;
    installedAppNames: string[];
    isLoading: boolean;
    loadInstalledAppNamesHandler: (() => void) | null;
    phiNativeResult: string;
    phiNativeStatus: string;
    previousProfileId: string | null;
    tryCommandHandlers: ((command: string, profile: UserDeviceProfileModel) => boolean)[];
    userLicenseAccess: IUserAccess | undefined;

    // Actions
    addTryCommandHandler: (handler: (command: string, profile: UserDeviceProfileModel) => boolean) => void;
    clearAll: () => void;
    clearListeners: () => void;
    getFavoritesPayload: () => { profileId: string; favorites: Record<string, boolean> } | null;
    loadInstalledAppNames: () => void;
    resetCurrentProfile: () => Promise<IResult<UserDeviceModel>>;
    resetPhiResult: () => void;
    setCurrentDomain: (v: string | undefined) => void;
    setFavoritesDirty: (v: boolean) => void;
    updateAppTracking: (
        currentApp: string | undefined,
        previousApp: string | undefined,
        currentDomain: string | undefined
    ) => void;
    setInstalledAppNames: (names: string[]) => void;
    setIsLoading: (v: boolean) => void;
    setLoadInstalledAppNamesHandler: (handler: (() => void) | null) => void;
    setPhiNativeResult: (v: string) => void;
    setPhiNativeStatus: (v: string) => void;
    setPreviousProfileId: (id: string | null) => void;
    setUserLicenseAccess: (access: IUserAccess | undefined) => void;
    stopAllOutputs: () => Promise<boolean>;
    toggleMacroFavorite: (macroId: string) => void;
    tryDeviceCommand: (command: string, additionalInputContent?: string) => Promise<boolean>;
    updateDeviceState: (device: UserDeviceModel | undefined, newProfile: UserDeviceProfileModel | null) => boolean;
    updatePhiResult: (token: string) => void;
}

export const useCurrentDeviceStore = create<CurrentDeviceState>()((set, get) => {
    const snapshotState = (profile: UserDeviceProfileModel | null) => {
        localStorage.setItem(DEVICE_CURRENT_PROFILE, JSON.stringify(profile));
    };

    // Maps macro id -> isFavorite for every macro in the profile that has an id.
    const buildFavoritesMap = (profile: UserDeviceProfileModel | null): Record<string, boolean> => {
        const favorites: Record<string, boolean> = {};
        for (const macro of profile?.configuration?.macros ?? []) {
            if (macro.id) favorites[macro.id] = !!macro.isFavorite;
        }
        return favorites;
    };

    const updateLocalState = (
        device: UserDeviceModel | undefined,
        newProfile: UserDeviceProfileModel | null
    ): boolean => {
        const previousProfile = get().currentProfile;
        const favoritesDirty = get().favoritesDirty;
        const existingProfileStr = JSON.stringify(previousProfile);

        // Preserve unsaved session favorites against same-profile syncs: an incoming sync
        // replaces `currentProfile` wholesale and would otherwise drop the toggled isFavorite
        // flags before we get a chance to persist them. Merge them back in by macro id.
        let incomingProfile = newProfile;
        const isSameProfile = !!newProfile?.id && newProfile.id === previousProfile?.id;
        if (favoritesDirty && isSameProfile && incomingProfile?.configuration?.macros) {
            const favorites = buildFavoritesMap(previousProfile);
            incomingProfile = {
                ...incomingProfile,
                configuration: {
                    ...incomingProfile.configuration,
                    macros: incomingProfile.configuration.macros.map((m) =>
                        m.id && m.id in favorites ? { ...m, isFavorite: favorites[m.id] } : m
                    ),
                },
            };
        }

        const newProfileStr = JSON.stringify(incomingProfile);
        let updatedProfile = previousProfile;

        if (existingProfileStr !== newProfileStr) {
            if (incomingProfile && isLicenseValidForProfile(incomingProfile, get().userLicenseAccess)) {
                updatedProfile = incomingProfile;
            } else {
                updatedProfile = null;
            }
            snapshotState(updatedProfile);
        }

        // When switching to a different profile, flush the outgoing profile's unsaved favorite
        // changes so the main window can persist them via the API (handled over IPC in the panel).
        const isProfileSwitch = (previousProfile?.id ?? null) !== (updatedProfile?.id ?? null);
        if (favoritesDirty && isProfileSwitch && previousProfile?.id) {
            publish('favoritesNeedSave', {
                profileId: previousProfile.id,
                favorites: buildFavoritesMap(previousProfile),
            });
        }

        set((state) => ({
            device,
            id: device?.id,
            deviceTypeId: device?.device?.deviceType.id,
            currentProfile: updatedProfile,
            // The dirty flag belongs to the profile we were on; reset it on a profile switch.
            favoritesDirty: isProfileSwitch ? false : state.favoritesDirty,
        }));

        publish('currentDeviceUpdated', { device, profile: updatedProfile });
        return true;
    };

    const setIsLoading = (v: boolean) => set({ isLoading: v });

    const setPreviousProfileId = (id: string | null) => set({ previousProfileId: id });

    const setFavoritesDirty = (v: boolean) => set({ favoritesDirty: v });

    // Snapshot of the current profile's favorite flags, for relaying to the main window
    // (the single API writer) so it can persist them. Returns null if there is no profile id.
    const getFavoritesPayload = (): { profileId: string; favorites: Record<string, boolean> } | null => {
        const profile = get().currentProfile;
        if (!profile?.id) return null;
        return { profileId: profile.id, favorites: buildFavoritesMap(profile) };
    };

    const setInstalledAppNames = (names: string[]) => set({ installedAppNames: names });

    const setPhiNativeResult = (v: string) => set({ phiNativeResult: v });

    const setPhiNativeStatus = (v: string) => set({ phiNativeStatus: v });

    const updatePhiResult = (token: string) => {
        const newVal = get().phiNativeResult + token;
        set({ phiNativeResult: newVal });
    };

    const setCurrentDomain = (v: string | undefined) => set({ currentDomain: v });

    const resetPhiResult = () => set({ phiNativeResult: '' });

    const updateAppTracking = (
        currentApp: string | undefined,
        previousApp: string | undefined,
        currentDomain: string | undefined
    ) =>
        set(() => ({
            previousApp,
            currentApp,
            currentDomain,
        }));

    const setUserLicenseAccess = (access: IUserAccess | undefined) => set({ userLicenseAccess: access });

    const addTryCommandHandler = (handler: (command: string, profile: UserDeviceProfileModel) => boolean) =>
        set((state) => ({ tryCommandHandlers: [...state.tryCommandHandlers, handler] }));

    const setLoadInstalledAppNamesHandler = (handler: (() => void) | null) => {
        set({ loadInstalledAppNamesHandler: handler });
    };

    const loadInstalledAppNames = () => {
        const handler = get().loadInstalledAppNamesHandler;
        if (handler) {
            handler();
        }
    };

    const updateDeviceState = (
        device: UserDeviceModel | undefined,
        newProfile: UserDeviceProfileModel | null
    ): boolean => {
        const currentState = get(); // Get current Zustand state (source of truth)
        let isDeviceEqual = false;
        if (device && currentState.device) {
            const isIdEqual = device.id === currentState.device.id;
            const isModifiedDateEqual = device.modifiedDate === currentState.device.modifiedDate;
            isDeviceEqual = isIdEqual && isModifiedDateEqual;
        }
        const isProfileEqual = _.isEqual(newProfile, currentState.currentProfile);
        if (isDeviceEqual && isProfileEqual) return false;
        return updateLocalState(device, newProfile);
    };

    const tryDeviceCommand = async (command: string, additionalInputContent?: string): Promise<boolean> => {
        const { tryCommandHandlers } = get();
        const currentProfile = get().currentProfile;
        if (!command || !currentProfile) return false; // If command or profile is not available, cannot execute command
        if (currentProfile && get().id && get().device) {
            if (!currentProfile) return false;
            for (const handler of tryCommandHandlers) {
                if (handler(command, currentProfile)) return true;
            }
        }
        return false;
    };

    const stopAllOutputs = async (): Promise<boolean> => {
        return await get().tryDeviceCommand(StopAllOutputsCommand);
    };

    // Session-only favorites toggle: flips `isFavorite` on the macro inside `currentProfile`.
    // Intentionally does NOT snapshot to localStorage — favorites live only for the session and
    // are persisted to the API on profile switch / sign-out / edit (see `favoritesDirty`).
    // `updateLocalState` merges these flags back in by macro id on same-profile syncs so they
    // are not dropped by incoming SignalR updates before they can be saved.
    const toggleMacroFavorite = (macroId: string) => {
        const currentProfile = get().currentProfile;
        const macros = currentProfile?.configuration?.macros;
        if (!currentProfile || !macros) return;
        set({
            currentProfile: {
                ...currentProfile,
                configuration: {
                    ...currentProfile.configuration,
                    macros: macros.map((m) => (m.id === macroId ? { ...m, isFavorite: !m.isFavorite } : m)),
                },
            },
            favoritesDirty: true,
        });
    };

    const resetCurrentProfile = async (): Promise<IResult<UserDeviceModel>> => {
        const id = get().id;
        if (!id || !get().device) {
            return new InvalidResult<UserDeviceModel>('Device ID is not set');
        }
        const currentDeviceProfile = get().currentProfile;
        let result: IResult<UserDeviceModel>;
        if (currentDeviceProfile?.isPublic) {
            result = await enabledApi.updateDeviceProfile(id, { defaultProfileId: null });
        } else {
            result = await enabledApi.updateDeviceProfile(id, { customProfileId: null });
        }

        if (result.resultType === 'Ok' && result.data) {
            updateLocalState(result.data, null);
        } else if (result.resultType !== 'Ok') {
            const currentDevice = get().device;
            if (!currentDevice) {
                return new InvalidResult<UserDeviceModel>('Device not found');
            }
            const updatedDeviceState = { ...currentDevice };
            if (currentDeviceProfile?.isPublic) {
                updatedDeviceState.defaultProfileId = null;
            } else {
                updatedDeviceState.currentProfile = null;
            }
            updateLocalState(updatedDeviceState, null);
            result = new SuccessResult<UserDeviceModel>(updatedDeviceState);
        }
        return result;
    };

    const clearListeners = () => set({ tryCommandHandlers: [] });

    const clearAll = () => {
        set({
            device: undefined,
            currentProfile: null,
            previousProfileId: null,
            errors: [],
            favoritesDirty: false,
            id: undefined,
            deviceTypeId: undefined,
            isLoading: false,
            installedAppNames: [],
            tryCommandHandlers: [],
            loadInstalledAppNamesHandler: null,
            currentApp: undefined,
            currentDomain: undefined,
            previousApp: undefined,
            phiNativeResult: '',
            phiNativeStatus: '',
            userLicenseAccess: undefined,
        });
        localStorage.removeItem(DEVICE_CURRENT_PROFILE);
    };

    return {
        // State
        device: undefined,
        isLoading: false,
        previousProfileId: null,
        errors: [],
        favoritesDirty: false,
        id: undefined,
        deviceTypeId: undefined,
        currentProfile: null,
        currentApp: undefined,
        currentDomain: undefined,
        previousApp: undefined,
        installedAppNames: [],
        tryCommandHandlers: [],
        loadInstalledAppNamesHandler: null,
        phiNativeResult: '',
        phiNativeStatus: '',
        userLicenseAccess: undefined,

        // Actions
        addTryCommandHandler,
        clearAll,
        clearListeners,
        getFavoritesPayload,
        loadInstalledAppNames,
        resetCurrentProfile,
        resetPhiResult,
        setCurrentDomain,
        setFavoritesDirty,
        updateAppTracking,
        setInstalledAppNames,
        setIsLoading,
        setLoadInstalledAppNamesHandler,
        setPhiNativeResult,
        setPhiNativeStatus,
        setPreviousProfileId,
        setUserLicenseAccess,
        stopAllOutputs,
        toggleMacroFavorite,
        tryDeviceCommand,
        updateDeviceState,
        updatePhiResult,
    };
});

// Module-level event subscriptions (replace useEffect([], []))
subscribe('userLicenseChangeLocal', async (data) => {
    if (data) {
        useCurrentDeviceStore.getState().setUserLicenseAccess(data);
        if (data.type === 'personal' && isLicensedProfile(useCurrentDeviceStore.getState().currentProfile)) {
            await useCurrentDeviceStore.getState().resetCurrentProfile();
        }
    }
});

subscribe('profileDeleted', async (data) => {
    if (data && data.profile?.id === useCurrentDeviceStore.getState().currentProfile?.id) {
        await useCurrentDeviceStore.getState().resetCurrentProfile();
    }
});
